import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { ConnectionState, GlobalNotificationManager } from './icons/icon-notification-signal';
import { LangOfflineOverlay } from '../../../localization/webmodule-common/other/ui/offline-overlay-lang';
import { ReconnectionState, WMEventSourceClient } from '../general/event-source-client';

const OVERLAY_ID = 'wm-offline-overlay';
const EXTENDED_OFFLINE_THRESHOLD = 60000; // 1 minute before showing "taking longer than expected"

/**
 * Initializes the offline overlay system.
 * Call this once at app startup to automatically show/hide the overlay
 * based on the GlobalNotificationManager connection state.
 *
 * The overlay is driven by the WMEventSourceClient - when the event source
 * goes offline, the overlay appears. When it reconnects, the overlay hides.
 *
 * Note: The overlay does NOT show on initial startup - only when connection
 * is lost after we've attempted to connect. This prevents blocking the
 * initialization screen before the first connection attempt.
 */
export function initOfflineOverlaySystem(eventSourceInstanceName?: string): () => void {
  const manager = GlobalNotificationManager.getInstance(eventSourceInstanceName);

  const unsubscribe = manager.onConnectionStateChange(state => {
    if (state === ConnectionState.Online) {
      hideOfflineOverlay();
    } else {
      // Only show overlay if we've been online before (i.e., we lost connection)
      showOfflineOverlay(eventSourceInstanceName);
    }
  });

  return unsubscribe;
}

/**
 * Shows the offline overlay on the page.
 * The overlay will automatically hide when connection is restored.
 * @param eventSourceInstanceName - Optional name of the WMEventSourceClient instance to get reconnection state from
 */
export function showOfflineOverlay(eventSourceInstanceName?: string): void {
  // Don't show multiple overlays
  if (document.getElementById(OVERLAY_ID)) return;

  const overlay = document.createElement('wm-offline-overlay') as WmOfflineOverlay;
  overlay.id = OVERLAY_ID;
  overlay.eventSourceInstanceName = eventSourceInstanceName;
  document.body.appendChild(overlay);
}

/**
 * Hides the offline overlay if it exists.
 */
export function hideOfflineOverlay(): void {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    overlay.remove();
  }
}

/**
 * Checks if the offline overlay is currently shown.
 */
export function isOfflineOverlayShown(): boolean {
  return document.getElementById(OVERLAY_ID) !== null;
}

@customElement('wm-offline-overlay')
export class WmOfflineOverlay extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      font-family:
        system-ui,
        -apple-system,
        sans-serif;
      color: white;
    }

    .container {
      text-align: center;
      max-width: 500px;
      padding: 2rem;
    }

    .icon {
      font-size: 4rem;
      margin-bottom: 1.5rem;
      animation: pulse 2s infinite;
    }

    .icon.warning {
      color: #ffc107;
    }

    .icon.error {
      color: #dc3545;
      animation: none;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }

    h1 {
      font-size: 1.75rem;
      font-weight: 600;
      margin: 0 0 1rem 0;
    }

    p {
      font-size: 1rem;
      color: #adb5bd;
      margin: 0 0 1.5rem 0;
      line-height: 1.5;
    }

    .status {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: #6c757d;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid #6c757d;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .retry-button {
      margin-top: 1.5rem;
      padding: 0.75rem 1.5rem;
      background: #0d6efd;
      color: white;
      border: none;
      border-radius: 0.375rem;
      font-size: 1rem;
      cursor: pointer;
      transition: background 0.2s;
    }

    .retry-button:hover {
      background: #0b5ed7;
    }

    .retry-button:disabled {
      background: #6c757d;
      cursor: not-allowed;
    }

    .extended-message {
      margin-top: 1rem;
      padding: 1rem;
      background: rgba(255, 193, 7, 0.1);
      border: 1px solid rgba(255, 193, 7, 0.3);
      border-radius: 0.375rem;
      color: #ffc107;
      font-size: 0.875rem;
    }

    .retry-info {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: #6c757d;
    }
  `;

  /** Name of the WMEventSourceClient instance to track */
  eventSourceInstanceName?: string;

  @state() private _reconnectionState: ReconnectionState = {
    attemptCount: 0,
    nextRetrySeconds: 1,
    isReconnecting: false
  };
  @state() private _showExtendedMessage = false;
  @state() private _connectionState: ConnectionState = ConnectionState.Offline;

  private _extendedTimer?: number;
  private _countdownTimer?: number;
  private _unsubscribeConnection?: () => void;
  private _unsubscribeReconnection?: () => void;

  connectedCallback(): void {
    super.connectedCallback();

    // Show extended message after threshold
    this._extendedTimer = window.setTimeout(() => {
      this._showExtendedMessage = true;
    }, EXTENDED_OFFLINE_THRESHOLD);

    // Update countdown every second for smooth countdown display
    this._countdownTimer = window.setInterval(() => {
      this.requestUpdate();
    }, 1000);

    // Subscribe to connection state changes
    const notificationManager = GlobalNotificationManager.getInstance(this.eventSourceInstanceName);
    this._connectionState = notificationManager.connectionState;

    this._unsubscribeConnection = notificationManager.onConnectionStateChange(state => {
      this._connectionState = state;
      if (state === ConnectionState.Online) {
        // Auto-hide handled by the overlay system, just update state
        this.requestUpdate();
      }
    });

    // Subscribe to reconnection state from event source
    try {
      const eventSource = WMEventSourceClient.getInstance(this.eventSourceInstanceName ?? 'default');
      this._reconnectionState = eventSource.reconnectionState;

      this._unsubscribeReconnection = eventSource.onReconnectionStateChange(state => {
        this._reconnectionState = state;
      });
    } catch {
      // Event source may not be initialized yet, that's ok
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanup();
  }

  private _cleanup(): void {
    if (this._extendedTimer) {
      clearTimeout(this._extendedTimer);
      this._extendedTimer = undefined;
    }
    if (this._countdownTimer) {
      clearInterval(this._countdownTimer);
      this._countdownTimer = undefined;
    }
    if (this._unsubscribeConnection) {
      this._unsubscribeConnection();
      this._unsubscribeConnection = undefined;
    }
    if (this._unsubscribeReconnection) {
      this._unsubscribeReconnection();
      this._unsubscribeReconnection = undefined;
    }
  }

  private _getStatusMessage(): string {
    const { attemptCount, isReconnecting, nextRetryScheduledTime } = this._reconnectionState;

    if (isReconnecting) {
      return LangOfflineOverlay.attemptingReconnect(attemptCount);
    }

    // Use the scheduled time for accurate countdown
    if (nextRetryScheduledTime) {
      const nextRetryIn = Math.max(0, Math.ceil((nextRetryScheduledTime.getTime() - Date.now()) / 1000));
      if (nextRetryIn > 0) {
        return LangOfflineOverlay.nextRetryIn(nextRetryIn);
      }
    }

    return LangOfflineOverlay.attemptingReconnect(attemptCount);
  }

  render() {
    const isExtended = this._showExtendedMessage;
    const isServiceRestarting = this._connectionState === ConnectionState.ServiceRestarting;

    return html`
      <div class="container">
        <div class="icon ${isExtended ? 'error' : 'warning'}">
          <i
            class="fa-solid ${isExtended
              ? 'fa-plug-circle-xmark'
              : isServiceRestarting
                ? 'fa-rotate'
                : 'fa-cloud-arrow-down'}"
          ></i>
        </div>

        <h1>
          ${isServiceRestarting
            ? LangOfflineOverlay.serviceRestartingTitle()
            : isExtended
              ? LangOfflineOverlay.connectionLostTitle()
              : LangOfflineOverlay.connectingTitle()}
        </h1>

        <p>
          ${isServiceRestarting
            ? LangOfflineOverlay.serviceRestartingMessage()
            : isExtended
              ? LangOfflineOverlay.connectionLostMessage()
              : LangOfflineOverlay.connectingMessage()}
        </p>

        <div class="status">
          <div class="spinner"></div>
          <span>${this._getStatusMessage()}</span>
        </div>

        ${this._reconnectionState.attemptCount > 0
          ? html`
              <div class="retry-info">
                ${LangOfflineOverlay.reconnectionAttempts(this._reconnectionState.attemptCount)}
              </div>
            `
          : null}
        ${isExtended && !isServiceRestarting
          ? html` <div class="extended-message">${LangOfflineOverlay.extendedOfflineMessage()}</div> `
          : null}
      </div>
    `;
  }
}
